import type { SplitButtonProps as PrimeSplitButtonProps } from 'primereact/splitbutton';
import { SplitButton as PrimeSplitButton } from 'primereact/splitbutton';
import clsx from 'clsx';
import type { ButtonVariant } from '../button/button';

export interface SplitButtonProps
  extends Omit<PrimeSplitButtonProps, 'text' | 'rounded' | 'raised' | 'outlined' | 'severity' | 'size'> {
  variant?: ButtonVariant;
}

const commonPrimaryButtonStyles =
  'sm:odin-w-auto odin-text-base odin-leading-5 odin-rounded focus:odin-outline-none odin-justify-center odin-transition-colors odin-duration-200 odin-min-w-[3rem] odin-min-h-[2.25rem] odin-py-2 odin-px-5 odin-font-medium';
const primaryButtonStyles = {
  regular: 'odin-bg-primary-2 hover:odin-bg-primary-3 odin-text-mono-1',
  alternative: 'odin-bg-primary-1 hover:odin-bg-primary-2 odin-text-mono-1',
};

const commonMenuButtonStyles = 'odin-rounded-r odin-py-2 odin-px-4';
const menuButtonStyles = {
  regular: 'odin-bg-primary-2 hover:odin-bg-primary-3 odin-text-mono-1',
  alternative: 'odin-bg-primary-1 hover:odin-bg-primary-2 odin-text-mono-1',
};

const menuStyles = 'odin-shadow-md odin-rounded-md';

/**
 * A wrapper component for PrimeReact's SplitButton.
 *
 * This component takes all the properties of `PrimeSplitButton` and passes them through.
 *
 * @component
 * @param {SplitButtonProps} props - The properties passed to the SplitButton component.
 * @returns {React.JSX.Element} A JSX element representing the SplitButton.
 *
 * @example
 * ```tsx
 * <SplitButton label="Options" model={items} variant="alternative" />
 * ```
 *
 * @see https://v9.primereact.org/splitbutton/#basic
 */
const SplitButton = ({ variant = 'regular', className, ...rest }: SplitButtonProps) => {
  return (
    <PrimeSplitButton
      {...rest}
      className={clsx('odin', className)}
      buttonClassName={clsx(commonPrimaryButtonStyles, primaryButtonStyles[variant])}
      menuButtonClassName={clsx(commonMenuButtonStyles, menuButtonStyles[variant])}
      menuClassName={clsx('odin', menuStyles)}
    />
  );
};

export default SplitButton;
